import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import isEmail from 'validator/lib/isEmail';
import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import styles from './ContactsExportModal.module.css';
import { CloseOutlined, SendOutlined } from '@ant-design/icons';
import { PrimaryButton } from '../../buttons/PrimaryButton/PrimaryButton';
import { LeadsCreationPendingInfoAlert } from '../../../editor/features/beta/modules/LeadsSearch/PurchaseContactsTable/LeadsCreationPendingInfoAlert/LeadsCreationPendingInfoAlert';
import { Input } from '@mm/components';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { CloseModalProps } from '../modal-helpers';
import { useDebounce } from '@mm/hooks';

type Props = {
  audienceId: number;
  contactsCount?: number;
  onSuccess?: () => void;
};

export const ContactsExportModal = (props: Props & CloseModalProps) => {
  const { closeModal, onSuccess, audienceId, contactsCount } = props;
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const botId = useSelector(activeChatbotIdSelector);
  const { createScheduledExport } = useContactsCRUDActions();
  const { createScheduledExport: exportAudience } = useAudiencesCRUDActions();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const debouncedEmail = useDebounce(email.trim(), 300);

  const scheduleExport = async (inputEmail: string, onSuccess?: () => void) => {
    const email = inputEmail && inputEmail.trim();
    if (email) {
      if (isEmail(email)) {
        if (facebookPageId && !audienceId) {
          await createScheduledExport(facebookPageId, email);
          onSuccess?.();
        } else if (botId && audienceId) {
          await exportAudience(botId, audienceId, email);
          onSuccess?.();
        }
        closeModal();
      }
    }
  };

  useEffect(() => {
    if (debouncedEmail) {
      if (isEmail(debouncedEmail)) {
        setError('');
      } else {
        setError('Please enter a valid email.');
      }
    }
  }, [debouncedEmail]);

  return (
    <div className={styles.container}>
      <CloseOutlined className={styles.closeIcon} onClick={closeModal} />

      <div className={styles.header}>
        <div className={styles.title}>
          Exporting {contactsCount?.toLocaleString('en-US')}{' '}
          {contactsCount === 1 ? 'lead' : 'leads'}
        </div>
      </div>

      <LeadsCreationPendingInfoAlert
        title="Your leads are being added to CSV file."
        description="It could take some time. We will email you when it’s ready."
      />

      <div className={styles.audienceNameWrapper}>
        Enter the email address you’d like us to send your download link to
        <Input
          css={{
            input: styles.audienceEditBarInput,
            container: styles.audienceEditBar,
          }}
          value={email}
          onChange={e => setEmail(e.target.value)}
          invalid={!!error}
          placeholder="Enter email address"
        />
        {error && <div className={styles.error}>{error}</div>}
      </div>

      <div className={styles.buttons}>
        <PrimaryButton
          onClick={() => scheduleExport(email, onSuccess)}
          className={styles.primaryBtn}
          disabled={!!error || !email}
        >
          <SendOutlined className={styles.arrow} />
          Send
        </PrimaryButton>
      </div>
    </div>
  );
};
